import type { Structure, TrustLossLedgerEntry } from "../types/entities";

/**
 * B146 (Day 80) — Normalize a raw FY string to canonical "YYYY-YY" form.
 *
 * Recognised inputs:
 * - "2023-24"   → "2023-24" (canonical, passthrough)
 * - "FY23-24"   → "2023-24"
 * - "FY2023-24" → "2023-24"
 * - "2023/24"   → "2023-24"
 * - "23-24"     → "2023-24" (assume 20XX prefix)
 *
 * Unrecognised → returns input verbatim (passthrough). No throw.
 *
 * Pure. Defensive — used by applyEntityLossCarryForward to compare
 * ledger entry FYs against the requested FY param. Storage of
 * entry.fy is preserved as-is; comparison is normalised.
 */
export function normalizeFY(raw: string): string {
  if (!raw || typeof raw !== "string") return raw;
  const s = raw.trim();
  // Canonical "YYYY-YY"
  if (/^\d{4}-\d{2}$/.test(s)) return s;
  // "FY2023-24" or "FY 2023-24"
  let m = s.match(/^FY\s*(\d{4})-(\d{2})$/i);
  if (m) return `${m[1]}-${m[2]}`;
  // "FY23-24" or "FY 23-24"
  m = s.match(/^FY\s*(\d{2})-(\d{2})$/i);
  if (m) return `20${m[1]}-${m[2]}`;
  // "2023/24"
  m = s.match(/^(\d{4})\/(\d{2})$/);
  if (m) return `${m[1]}-${m[2]}`;
  // "23-24" → "2023-24"
  m = s.match(/^(\d{2})-(\d{2})$/);
  if (m) return `20${m[1]}-${m[2]}`;
  // Unrecognised — passthrough
  return s;
}

/**
 * B125 — Apply trust loss carry-forward against current-FY gross net income.
 *
 * Authority: ITAA 1997 Div 36; s.36-17 (FIFO — losses applied oldest-first);
 * Schedule 2F ITAA 1936 (trust loss measures); ATO TR 95/D6.
 *
 * Logic:
 *   1. SMSF: passthrough — Div 295 has different rules.
 *   2. No ledger / empty ledger: passthrough.
 *   3. grossNetIncome <= 0: passthrough — caller (B121 resolver Layer 1)
 *      handles loss-quarantine. This FY's loss is NOT auto-added to ledger
 *      (user enters lodged figure post-FY-end).
 *   4. grossNetIncome > 0: walk prior-FY entries (entry.fy < requestedFy)
 *      in FY ascending order; consume `remaining` against gross until
 *      gross is zero or no entries remain. Build a copy of the ledger
 *      with `applied[requestedFy]` and updated `remaining`.
 *
 * Pure — does NOT mutate input. Returned `nextLedger` is what the caller
 * would persist to lock in this FY's application (V1 doesn't auto-persist).
 */
export type LossCarryForwardResult = {
  adjustedNetIncome: number;
  lossesAppliedThisFY: number;
  totalCarriedAtEnd: number;
  perEntryApplied: Array<{ fy: string; appliedThisFY: number; remainingAfter: number }>;
  nextLedger: TrustLossLedgerEntry[];
};

export function applyEntityLossCarryForward(
  structure: Pick<Structure, "entityType" | "lossCarryForward">,
  fy: string,
  grossNetIncome: number,
): LossCarryForwardResult {
  const passthrough: LossCarryForwardResult = {
    adjustedNetIncome: grossNetIncome,
    lossesAppliedThisFY: 0,
    totalCarriedAtEnd: 0,
    perEntryApplied: [],
    nextLedger: structure.lossCarryForward ?? [],
  };

  // Layer 1 — SMSF passthrough.
  if (structure.entityType === "SMSF") return passthrough;

  // Layer 2 — no ledger.
  const ledger = structure.lossCarryForward;
  if (!ledger || ledger.length === 0) return passthrough;

  // Compute current totalCarried regardless of whether we apply.
  const sumRemaining = (entries: TrustLossLedgerEntry[]): number =>
    entries.reduce((s, e) => s + Math.max(0, e.remaining), 0);

  // Layer 3 — gross <= 0: passthrough but report total carried.
  if (grossNetIncome <= 0) {
    return {
      ...passthrough,
      totalCarriedAtEnd: sumRemaining(ledger),
    };
  }

  // Layer 4 — apply FIFO. Only entries with fy < requestedFy are eligible.
  // B146 (Day 80): normalise both sides of the lex compare. Storage of
  // entry.fy preserved as-is (no migration); comparison + per-FY applied
  // map key use the canonical "YYYY-YY" form.
  const normFy = normalizeFY(fy);
  const eligible = ledger
    .filter((e) => normalizeFY(e.fy) < normFy)
    .sort((a, b) => normalizeFY(a.fy).localeCompare(normalizeFY(b.fy))); // oldest first

  let remainingGross = grossNetIncome;
  let lossesAppliedThisFY = 0;
  const perEntryApplied: LossCarryForwardResult["perEntryApplied"] = [];
  const consumed = new Map<string, number>();

  for (const entry of eligible) {
    if (remainingGross <= 0) break;
    const available = Math.max(0, entry.remaining);
    if (available <= 0) continue;
    const used = Math.min(available, remainingGross);
    consumed.set(entry.fy, used);
    remainingGross -= used;
    lossesAppliedThisFY += used;
    perEntryApplied.push({
      fy: entry.fy,
      appliedThisFY: used,
      remainingAfter: available - used,
    });
  }

  // Build nextLedger — pure, never mutates input.
  // B146: applied[<key>] uses the normalised requested FY so the per-FY
  // breakdown key is canonical regardless of caller's input format.
  const nextLedger: TrustLossLedgerEntry[] = ledger.map((e) => {
    const used = consumed.get(e.fy);
    if (!used) return e;
    const nextApplied = { ...(e.applied ?? {}), [normFy]: (e.applied?.[normFy] ?? 0) + used };
    return {
      ...e,
      applied: nextApplied,
      remaining: Math.max(0, e.remaining - used),
    };
  });

  return {
    adjustedNetIncome: remainingGross,
    lossesAppliedThisFY,
    totalCarriedAtEnd: sumRemaining(nextLedger),
    perEntryApplied,
    nextLedger,
  };
}
